import React, { useId } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Menu,
  Tooltip,
  IconButton,
  Avatar,
  CircleBadge,
  Box,
  HStack,
  VStack,
  Text,
  Portal,
} from "@commonsku/design-system";
import { Bell } from "lucide-react";
import { LuUser } from "react-icons/lu";
import { clearNotifications, dismissNotification } from "../../actions/header";
import { getIdentityUtils, getImageSrcByImgPaths } from "../../utils";
import { useIdentity } from "../../hooks";

const DEFAULT_AVATAR_SRC = "/images/user-avatar3-36x36.png";

// Brand-blue text with no underline/focus ring for notification menu items.
function LinkText({ children, ...props }: React.ComponentProps<typeof Text>) {
  return (
    <Text
      m="0"
      fontFamily="body"
      color="fg.link"
      textDecoration="none"
      {...props}
    >
      {children}
    </Text>
  );
}

function NotificationMenuItem({
  children,
  ...props
}: React.ComponentProps<typeof Menu.Item>) {
  return (
    <Menu.Item p="6px" cursor="pointer" {...props}>
      {children}
    </Menu.Item>
  );
}

export interface CommunityNotification {
  notification_id: string;
  parent_id: string;
  parent_type: string;
  user_id: string;
  notification_text: string;
  date_modified: string;
  active: string | number;
  avatar?: { small?: string } | null;
}

interface CommunityNotificationsRootState {
  entities: {
    notifications: Record<string, CommunityNotification>;
    notification_count: number;
  };
}

const BUTTON_LABEL = "Notifications";

/**
 * Nav bar menu showing the signed-in user's skummunity forum notifications,
 * per CORE-1925.
 */
function CommunityNotificationsButton() {
  const triggerId = useId();
  const dispatch = useDispatch();
  const identity = useIdentity();
  const { hasCapabilities } = getIdentityUtils(identity);
  // Same gate as the "Newsfeed" nav item in the old nav (csku_menu_utils.js).
  const canAccessNewsfeed = !hasCapabilities(["FEATURE-LIMITED"]);

  const notifications = useSelector(
    (state: CommunityNotificationsRootState) => state.entities.notifications,
  );
  const notificationCount = useSelector(
    (state: CommunityNotificationsRootState) =>
      state.entities.notification_count,
  );

  const notificationList = Object.values(notifications || {});

  if (!canAccessNewsfeed) {
    return null;
  }

  return (
    <Menu.Root
      ids={{ trigger: triggerId }}
      positioning={{ placement: "bottom-end", gutter: 8 }}
    >
      <CircleBadge count={notificationCount} color="brand" size="small">
        <Tooltip.Root
          ids={{ trigger: triggerId }}
          positioning={{ placement: "bottom-end", strategy: "fixed" }}
        >
          <Tooltip.Trigger asChild>
            <Menu.Trigger asChild>
              <IconButton
                label={BUTTON_LABEL}
                aria-label={BUTTON_LABEL}
                variant="outline"
                colorPalette="neutral"
                size="sm"
                rounded="md"
              >
                <Bell size={18} aria-hidden="true" />
              </IconButton>
            </Menu.Trigger>
          </Tooltip.Trigger>
          <Tooltip.Positioner>
            <Tooltip.Content>{BUTTON_LABEL}</Tooltip.Content>
          </Tooltip.Positioner>
        </Tooltip.Root>
      </CircleBadge>

      <Portal>
        <Menu.Positioner>
          <Menu.Content w="280px" p="8px" pr="20px">
            {notificationList.length === 0 ? (
              <Box p="8px">
                <Text fontSize="sm" m="0" fontFamily="body">
                  You do not have any notifications.
                </Text>
              </Box>
            ) : (
              <>
                {notificationList.map((notification) => (
                  <NotificationMenuItem
                    key={notification.notification_id}
                    value={notification.notification_id}
                    onClick={() => dispatch(dismissNotification(notification))}
                  >
                    <HStack align="start" gap="8px">
                      <Avatar
                        src={
                          typeof notification.avatar?.small === "string" &&
                          notification.avatar.small !== ""
                            ? getImageSrcByImgPaths(
                                notification.avatar,
                                DEFAULT_AVATAR_SRC,
                              )
                            : DEFAULT_AVATAR_SRC
                        }
                        icon={<LuUser size={12} />}
                        size="2xs"
                        shape="rounded"
                        flexShrink={0}
                        mt="4px"
                      />
                      <VStack align="start" gap="0" overflow="hidden">
                        <LinkText fontSize="sm">
                          {notification.notification_text}
                        </LinkText>
                      </VStack>
                    </HStack>
                  </NotificationMenuItem>
                ))}
                <Menu.Separator />
                <NotificationMenuItem
                  value="clear-notifications"
                  onClick={() => dispatch(clearNotifications(identity.user_id))}
                >
                  <LinkText fontSize="xs" fontWeight="medium">
                    Clear notifications
                  </LinkText>
                </NotificationMenuItem>
              </>
            )}
          </Menu.Content>
        </Menu.Positioner>
      </Portal>
    </Menu.Root>
  );
}

export default CommunityNotificationsButton;
